import { ROUTES } from "@/router/routes/constants";
import { accountRoutes } from "./account";
import { cartRoutes } from "./cart";
import { checkoutRoutes } from "./checkout";
import { corporateRoutes } from "./company";
import type { RouteRecordRaw } from "vue-router";
import Error400 from "@/pages/400.vue";
import Error403 from "@/pages/403.vue";
import Error404 from "@/pages/404.vue";
import Error500 from "@/pages/500.vue";

const callback = () => import("@/pages/auth/callback.vue");
const SingInPage = () => import("@/pages/sign-in.vue");
const InnoSignUpPage = () => import("@/pages/innovadis/inno-sign-up.vue"); // Innovadis custom: Custom sign up page
const ConfirmEmail = () => import("@/pages/confirm-email.vue");
const ConfirmInvitation = () => import("@/pages/confirm-invitation.vue");
const ForgotPassword = () => import("@/pages/forgot-password.vue");
const ResetPassword = () => import("@/pages/reset-password.vue");
const ChangePassword = () => import("@/pages/change-password.vue");
const BlockedPage = () => import("@/pages/blocked.vue");
const Account = () => import("@/pages/account/index.vue");
const Brands = () => import("@/pages/brands.vue");
const Company = () => import("@/pages/company/index.vue");
const BulkOrder = () => import("@/pages/bulk-order.vue");
const InnoCompareProducts = () => import("@/pages/innovadis/inno-compare-products.vue"); // Innovadis custom: Custom compare products page
const InnoCart = () => import("@/pages/innovadis/inno-cart.vue"); // Innovadis custom: Custom cart page
const InnoProduct = () => import("@/pages/innovadis/inno-product.vue"); // Innovadis custom: Custom product page
const Search = () => import("@/pages/search.vue");
const Catalog = () => import("@/pages/catalog.vue");
const InnoCategory = () => import("@/shared/catalog/components/innovadis/inno-category.vue"); // Innovadis custom: Load inno category directly
const Product = () => import("@/pages/product.vue");
const SharedList = () => import("@/pages/shared-list.vue");
const Branch = () => import("@/pages/branch.vue");
const Welcome = () => import("@/pages/welcome.vue");
const Matcher = () => import("@/pages/matcher/matcher.vue");
const OrderPaymentMollie = () => import("@/pages/checkout/payment-mollie.vue");
const ImpersonateUserDashboard = () => import("@/pages/impersonate-user.vue");
const D365Product = () => import("@/pages/innovadis/d365-product.vue");

// Innovadis custom remove slash before path to allow locales
export const mainRoutes: RouteRecordRaw[] = [
  { path: "auth/callback", name: "AuthCallback", component: callback, meta: { public: true } },
  { path: "/400", name: "BadRequest", component: Error400, meta: { public: true, redirectable: false } },
  { path: "/403", name: "NoAccess", component: Error403, meta: { public: true } },
  { path: "/404", name: "NotFound", component: Error404, meta: { public: true } },
  { path: "/500", name: "InternalError", component: Error500, meta: { public: true } },
  { path: ROUTES.SIGN_IN.PATH, name: ROUTES.SIGN_IN.NAME, component: SingInPage, meta: { public: true } },
  { path: "/sign-up", name: "SignUp", component: InnoSignUpPage, meta: { public: true } },
  { path: "confirm-invitation", name: "ConfirmInvitation", component: ConfirmInvitation, meta: { public: true } },
  { path: "/forgot-password", name: "ForgotPassword", component: ForgotPassword, meta: { public: true } },
  { path: "/reset-password", name: "ResetPassword", component: ResetPassword, meta: { public: true } },
  {
    path: ROUTES.CHANGE_PASSWORD.PATH,
    name: ROUTES.CHANGE_PASSWORD.NAME,
    component: ChangePassword,
    meta: { public: false, redirectable: false },
  },
  { path: "set-password", name: "SetPassword", component: ResetPassword, meta: { public: true } },
  { path: "blocked", name: "Blocked", component: BlockedPage, meta: { public: true } },
  { path: "account/confirmemail", name: "ConfirmEmail", component: ConfirmEmail, meta: { public: true } },
  {
    path: "account",
    name: "Account",
    component: Account,
    children: accountRoutes,
    redirect: { name: accountRoutes[0].name },
    meta: { requiresAuth: true },
  },
  {
    path: "company",
    name: "Company",
    component: Company,
    children: corporateRoutes,
    redirect: { name: corporateRoutes[0].name },
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
    },
  },
  { path: "branch/:branchId", name: "BranchPage", component: Branch, props: true },
  { path: ROUTES.SEARCH.PATH, name: ROUTES.SEARCH.NAME, component: Search },
  { path: "bulk-order", name: "BulkOrder", component: BulkOrder },
  { path: "compare", name: "CompareProducts", component: InnoCompareProducts },
  { path: "cart", name: "Cart", component: InnoCart },
  { path: "successful-registration", name: "Welcome", component: Welcome, meta: { public: true } },
  ...checkoutRoutes,
  { path: ROUTES.CATALOG.PATH, name: ROUTES.CATALOG.NAME, component: Catalog, props: true },
  {
    path: "category/:categoryId",
    name: "Category",
    component: InnoCategory,
    props: (route) => ({ categoryId: route.params.categoryId, allowSetMeta: true }),
  },
  { path: "brands", name: "Brands", component: Brands },
  {
    path: "cart/externalpaymentcallback",
    name: "OrderPaymentMollie",
    component: OrderPaymentMollie,
    meta: { public: true },
  },
  {
    path: "impersonate-user",
    name: "ImpersonateUserDashboard",
    component: ImpersonateUserDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/product/:productId",
    name: "Product",
    component: InnoProduct,
    props: (route) => ({ allowSetMeta: true, productId: route.params.productId }),
  },
  {
    path: "D365/product",
    name: "D365 Product",
    component: D365Product,
  },
  /** NOTE: Always leave it last. */
  { path: "/:pathMatch(.*)*", name: "Matcher", component: Matcher, props: true },
];
